import {
    ApolloClient,
    createHttpLink,
    InMemoryCache,
    NormalizedCacheObject,
} from '@apollo/client';
import BigNumber from 'bignumber.js';
import { ChainId } from '../constants';
import { gql } from 'graphql-tag';
import { useEffect, useState } from 'react';
import user from '@/stores/user';

// Subgraph endpoints for LayerZero OFT bridge
const OFT_SUBGRAPH_ENDPOINTS = {
    metis: 'https://graph.goat.network/subgraphs/id/QmTtRLgM4SZ3wpEVCxqoocAM3BJCptLcG8kUn9FiLTqb9o',
    goat: 'https://graph.goat.network/subgraphs/id/Qmbe8Apf1KyH9hj7Wfgo9hEnYbkXANTiBNu8YZVshDneUP',
};

export enum OftRecordStatus {
    pending = 0,
    finished = 1,
}

// OFT Sent Event from subgraph
interface OftSentEvent {
    id: string;
    contractAddress: string;
    fromAddress: string;
    guid: string;
    dstEid: string;
    amountSentLD: string;
    amountReceivedLD: string;
    blockNumber: string;
    timestamp: string;
    transactionHash: string;
}

// OFT Received Event from subgraph
interface OftReceivedEvent {
    id: string;
    contractAddress: string;
    toAddress: string;
    guid: string;
    srcEid: string;
    amountReceivedLD: string;
    blockNumber: string;
    timestamp: string;
    transactionHash: string;
}

// Unified OFT Bridge Record
export interface OftBridgeRecord {
    id: string; // transaction hash of the source transaction
    guid: string;
    from: string;
    to: string;
    amount: string;
    symbol: string;

    sourceChainId: ChainId;
    destinationChainId: ChainId;

    blockNumber: string;
    timestamp: number;

    status: OftRecordStatus;
    finishTimestamp?: number;
    finishBlockNumber?: string;
    finishTransactionHash?: string;
}

interface QueryResult {
    oftSentEvents: OftSentEvent[];
    oftReceivedEvents: OftReceivedEvent[];
}

export default function useGetOftBridgeHistoryRecords() {
    const [metisSubgraphClient, setMetisSubgraphClient] =
        useState<ApolloClient<NormalizedCacheObject>>();
    const [goatSubgraphClient, setGoatSubgraphClient] =
        useState<ApolloClient<NormalizedCacheObject>>();

    const [records, setRecords] = useState<OftBridgeRecord[]>([]);
    const [loading, setLoading] = useState(false);

    // Initialize Apollo clients
    useEffect(() => {
        const metisClient = new ApolloClient({
            link: createHttpLink({
                uri: OFT_SUBGRAPH_ENDPOINTS.metis,
            }),
            cache: new InMemoryCache(),
        });

        const goatClient = new ApolloClient({
            link: createHttpLink({
                uri: OFT_SUBGRAPH_ENDPOINTS.goat,
            }),
            cache: new InMemoryCache(),
        });

        setMetisSubgraphClient(metisClient);
        setGoatSubgraphClient(goatClient);
        setRecords([]);
        setLoading(true);
    }, [user.chainId]);

    // Query Metis -> Goat bridge records
    async function getMetisToGoatRecords(
        address: string,
    ): Promise<OftBridgeRecord[]> {
        if (!metisSubgraphClient || !goatSubgraphClient) return [];

        const userAddress = address.toLowerCase();
        const records: OftBridgeRecord[] = [];

        try {
            // Query oftSentEvents from Metis subgraph
            const metisSentQuery = await metisSubgraphClient.query<QueryResult>(
                {
                    query: gql`
                        query ($fromAddress: Bytes!) {
                            oftSentEvents(
                                where: { fromAddress: $fromAddress }
                                first: 100
                                orderBy: timestamp
                                orderDirection: desc
                            ) {
                                id
                                contractAddress
                                fromAddress
                                guid
                                dstEid
                                amountSentLD
                                amountReceivedLD
                                blockNumber
                                timestamp
                                transactionHash
                            }
                        }
                    `,
                    variables: {
                        fromAddress: userAddress,
                    },
                    fetchPolicy: 'no-cache',
                },
            );

            const sentEvents = metisSentQuery.data.oftSentEvents || [];

            if (sentEvents.length === 0) {
                return records;
            }

            // Collect all GUIDs from sent events
            const guids = sentEvents.map((event) => event.guid);

            // Query oftReceivedEvents from Goat subgraph using GUIDs
            const goatReceivedQuery =
                await goatSubgraphClient.query<QueryResult>({
                    query: gql`
                        query ($guids: [Bytes!]!) {
                            oftReceivedEvents(
                                where: { guid_in: $guids }
                                first: 100
                            ) {
                                id
                                contractAddress
                                toAddress
                                guid
                                srcEid
                                amountReceivedLD
                                blockNumber
                                timestamp
                                transactionHash
                            }
                        }
                    `,
                    variables: {
                        guids: guids,
                    },
                    fetchPolicy: 'no-cache',
                });

            const receivedEvents =
                goatReceivedQuery.data.oftReceivedEvents || [];

            // Create a map of received events by GUID for quick lookup
            const receivedEventsByGuid = new Map<string, OftReceivedEvent>();
            receivedEvents.forEach((event) => {
                receivedEventsByGuid.set(event.guid, event);
            });

            // Match sent events with received events
            for (const sentEvent of sentEvents) {
                const receivedEvent = receivedEventsByGuid.get(sentEvent.guid);

                const record: OftBridgeRecord = {
                    id: sentEvent.transactionHash,
                    guid: sentEvent.guid,
                    from: sentEvent.fromAddress,
                    to: receivedEvent
                        ? receivedEvent.toAddress
                        : sentEvent.fromAddress,
                    amount: new BigNumber(sentEvent.amountSentLD)
                        .dividedBy(new BigNumber(10).pow(18))
                        .toString(),
                    symbol: 'BANG', // OFT token symbol
                    sourceChainId: ChainId.metisMainnet,
                    destinationChainId: ChainId.goatedMainnet,
                    blockNumber: sentEvent.blockNumber,
                    timestamp: Number(sentEvent.timestamp) * 1000,
                    status: receivedEvent
                        ? OftRecordStatus.finished
                        : OftRecordStatus.pending,
                };

                if (receivedEvent) {
                    record.finishTimestamp =
                        Number(receivedEvent.timestamp) * 1000;
                    record.finishBlockNumber = receivedEvent.blockNumber;
                    record.finishTransactionHash =
                        receivedEvent.transactionHash;
                }

                records.push(record);
            }
        } catch (e) {
            console.error('Error fetching Metis -> Goat bridge records:', e);
        }

        return records;
    }

    // Query Goat -> Metis bridge records
    async function getGoatToMetisRecords(
        address: string,
    ): Promise<OftBridgeRecord[]> {
        if (!metisSubgraphClient || !goatSubgraphClient) return [];

        const userAddress = address.toLowerCase();
        const records: OftBridgeRecord[] = [];

        try {
            // Query oftSentEvents from Goat subgraph
            const goatSentQuery = await goatSubgraphClient.query<QueryResult>({
                query: gql`
                    query ($fromAddress: Bytes!) {
                        oftSentEvents(
                            where: { fromAddress: $fromAddress }
                            first: 100
                            orderBy: timestamp
                            orderDirection: desc
                        ) {
                            id
                            contractAddress
                            fromAddress
                            guid
                            dstEid
                            amountSentLD
                            amountReceivedLD
                            blockNumber
                            timestamp
                            transactionHash
                        }
                    }
                `,
                variables: {
                    fromAddress: userAddress,
                },
                fetchPolicy: 'no-cache',
            });

            const sentEvents = goatSentQuery.data.oftSentEvents || [];

            if (sentEvents.length === 0) {
                return records;
            }

            // Collect all GUIDs from sent events
            const guids = sentEvents.map((event) => event.guid);

            // Query oftReceivedEvents from Metis subgraph using GUIDs
            const metisReceivedQuery =
                await metisSubgraphClient.query<QueryResult>({
                    query: gql`
                        query ($guids: [Bytes!]!) {
                            oftReceivedEvents(
                                where: { guid_in: $guids }
                                first: 100
                            ) {
                                id
                                contractAddress
                                toAddress
                                guid
                                srcEid
                                amountReceivedLD
                                blockNumber
                                timestamp
                                transactionHash
                            }
                        }
                    `,
                    variables: {
                        guids: guids,
                    },
                    fetchPolicy: 'no-cache',
                });

            const receivedEvents =
                metisReceivedQuery.data.oftReceivedEvents || [];

            // Create a map of received events by GUID for quick lookup
            const receivedEventsByGuid = new Map<string, OftReceivedEvent>();
            receivedEvents.forEach((event) => {
                receivedEventsByGuid.set(event.guid, event);
            });

            // Match sent events with received events
            for (const sentEvent of sentEvents) {
                const receivedEvent = receivedEventsByGuid.get(sentEvent.guid);

                const record: OftBridgeRecord = {
                    id: sentEvent.transactionHash,
                    guid: sentEvent.guid,
                    from: sentEvent.fromAddress,
                    to: receivedEvent
                        ? receivedEvent.toAddress
                        : sentEvent.fromAddress,
                    amount: new BigNumber(sentEvent.amountSentLD)
                        .dividedBy(new BigNumber(10).pow(18))
                        .toString(),
                    symbol: 'BANG', // OFT token symbol
                    sourceChainId: ChainId.goatedMainnet,
                    destinationChainId: ChainId.metisMainnet,
                    blockNumber: sentEvent.blockNumber,
                    timestamp: Number(sentEvent.timestamp) * 1000,
                    status: receivedEvent
                        ? OftRecordStatus.finished
                        : OftRecordStatus.pending,
                };

                if (receivedEvent) {
                    record.finishTimestamp =
                        Number(receivedEvent.timestamp) * 1000;
                    record.finishBlockNumber = receivedEvent.blockNumber;
                    record.finishTransactionHash =
                        receivedEvent.transactionHash;
                }

                records.push(record);
            }
        } catch (e) {
            console.error('Error fetching Goat -> Metis bridge records:', e);
        }

        return records;
    }

    // Fetch all OFT bridge records
    async function getRecords(address: string) {
        try {
            setLoading(true);

            const [metisToGoatRecords, goatToMetisRecords] = await Promise.all([
                getMetisToGoatRecords(address),
                getGoatToMetisRecords(address),
            ]);

            // Combine all records and sort by timestamp
            const allRecords = [
                ...metisToGoatRecords,
                ...goatToMetisRecords,
            ].sort((a, b) => b.timestamp - a.timestamp);

            setRecords(allRecords);
            setLoading(false);
        } catch (e) {
            console.error('Error fetching OFT bridge records:', e);
            setLoading(false);
        }
    }

    useEffect(() => {
        if (metisSubgraphClient && goatSubgraphClient && user.address) {
            getRecords(user.address);
        }
    }, [metisSubgraphClient, goatSubgraphClient, user.address]);

    return {
        records,
        getRecords,
        loading,
    };
}
